import axios from 'axios';
import { isObject } from 'lodash';
import { objectToQuerystring } from '../utils';

import { getMetaDataFromGeoPortal } from './http.service';
import { cdnApiClient } from './cdnApiClient';
import { cdnApiPath } from './cdnApiPaths';

import appConfig from '../config';

// Remove values that are less than -998
const filterValues = (data) => {
  if (data.code) {
    return [];
  }
  if (data.length <= 0) {
    return [];
  }

  const newData = [];
  data.forEach((ds) => {
    const newParams = [];
    ds.parameters.forEach((item) => {
      const newObs = item.observations.filter((obs) => obs.value > -998);
      newParams.push({ parameter_id: item.parameter_id, observations: newObs });
    });
    newData.push({ obs_dataset_id: ds.obs_dataset_id, parameters: newParams });
  });
  return newData;
};

export const getObsData = (filter) => {
  const queryString = isObject(filter)
    ? objectToQuerystring(filter)
    : `startDate=${filter}`;

  return cdnApiClient.request(cdnApiPath(`obs-geo?${queryString}`))
    .then((data) => filterValues(data));
};

// Grab-sample (Omics/GLAMR) data. Same response shape as getObsData, but reads
// from sampling_data instead of observations -- for sampling_location platforms.
export const getSamplingData = (filter) => {
  const queryString = isObject(filter)
    ? objectToQuerystring(filter)
    : `startDate=${filter}`;

  return cdnApiClient.request(cdnApiPath(`sampling-data?${queryString}`))
    .then((data) => filterValues(data));
};

export const getObsLatestData = (collectionStatus = undefined) => {
  const path = collectionStatus
    ? `obs-latest?collectionStatus=${encodeURIComponent(collectionStatus)}`
    : 'obs-latest';

  return cdnApiClient.request(cdnApiPath(path))
    .then((data) => filterValues(data));
};

export const getObsDataV2 = (filter) => {
  const queryString = isObject(filter)
    ? objectToQuerystring(filter)
    : `startDate=${filter}`;
  return cdnApiClient.request(cdnApiPath(`obs?${queryString}`));
};

export const getObsDatasetsGeojson = () => cdnApiClient.request(cdnApiPath('obs-datasets.geojson'));

export const getObsDatasetsPlatformGeojson = (platformId) => cdnApiClient.request(cdnApiPath(`obs-datasets.geojson?platformId=${platformId}`));

export const getParameters = () => cdnApiClient.request(cdnApiPath('parameters'));

export const datasetTrajectoriesGeojson = () => cdnApiClient.request(cdnApiPath('obs-dataset-trajectories.geojson'));

export const datasets = () => cdnApiClient.request(cdnApiPath('obs-datasets'));

export const getDatasetsLatest = (filter) => {
  const queryString = objectToQuerystring(filter);
  return cdnApiClient.request(cdnApiPath(`obs-datasets-latest?${queryString}`));
};

export const getDatasetSummaries = (filter) => {
  const queryString = isObject(filter) ? `?${objectToQuerystring(filter)}` : '';
  return cdnApiClient.request(cdnApiPath(`obs-dataset-summaries${queryString}`));
};

export const parameterConfigurations = () => cdnApiClient.request(cdnApiPath('parameter-configurations'));

export const getParameterGroupConfigurations = () => cdnApiClient.request(cdnApiPath('parameter-configurations/groups'));

export const getRegisteredParameters = () => cdnApiClient.request(cdnApiPath('parameters'));

export const getExternalObsDatasets = () => cdnApiClient.request(cdnApiPath('ext-obs-datasets'));

export const getExternalParameters = () => cdnApiClient.request(cdnApiPath('ext-parameters'));

// v3 /ext-obs-latest returns { "<id>": { dataset, parameters: { "<paramId>": { value, timestamp, unit } } } }
// Normalize to the array shape callers expect: [{ ext_obs_dataset_id, ...dataset, parameters: [{ ext_parameter_id, observations: [{ value, timestamp }] }] }]
const normalizeExtObsLatest = (v3Data) => {
  if (!v3Data || Array.isArray(v3Data)) return v3Data ?? [];
  return Object.values(v3Data).map(({ dataset, parameters }) => ({
    ...dataset,
    parameters: Object.entries(parameters).map(([paramId, obs]) => ({
      ext_parameter_id: Number(paramId),
      observations: [{ value: obs.value, timestamp: obs.timestamp }],
    })),
  }));
};

export const getExternalObsLatestData = () => cdnApiClient.request(cdnApiPath('ext-obs-latest'))
  .then(normalizeExtObsLatest);

export const getExternalObs = (filter) => {
  const queryString = objectToQuerystring(filter);
  return cdnApiClient.request(cdnApiPath(`ext-obs?${queryString}`));
};

export const getObsWithQartod = (timestamp, parameterId) => cdnApiClient.request(cdnApiPath(`obs-with-qartod?timestamp=${timestamp}&parameterId=${parameterId}`));

export const searchData = (query) => cdnApiClient.request(cdnApiPath(`search?query=${query}`));

export const getPlatformMetadata = (id) => cdnApiClient.request(cdnApiPath(`obs-datasets/${id}/metadata`));

export const getDatasetFavoritesAnalytics = (obsDatasetId, startDate, endDate) => {
  const queryString = objectToQuerystring({ startDate, endDate });
  return cdnApiClient.request(cdnApiPath(`obs-datasets/${obsDatasetId}/analytics/favorites?${queryString}`));
};

export const getDatasetAlertsAnalytics = (obsDatasetId, startDate, endDate) => {
  const queryString = objectToQuerystring({ startDate, endDate });
  return cdnApiClient.request(cdnApiPath(`obs-datasets/${obsDatasetId}/analytics/alerts?${queryString}`));
};

export const getPlatformSponsors = () => axios
  .get('/data/sponsors.json')
  .then(({ data }) => data)
  .catch((err) => Promise.reject(err));

export const getPlatformWeather = () => axios
  .get('/data/weather.json')
  .then(({ data }) => data)
  .catch((err) => Promise.reject(err));

export const getAllNonFunctionalDatasets = () => axios
  .get('/data/dataset-detail.json')
  .then(({ data }) => data)
  .catch((err) => Promise.reject(err));

export const downloadNonFunctionalDataset = (id, filename) => cdnApiClient.request(cdnApiPath(`datasets/${id}/files?filename=${encodeURIComponent(filename)}`));

export const getHostedNonFunctionalDataset = (datasetId) => cdnApiClient.request(cdnApiPath(`datasets/${datasetId}`));

export const getNonFunctionalMetadata = (metadataId) => getMetaDataFromGeoPortal(`/rest/metadata/item/${metadataId}`);

export const searchNonFunctionalMetadata = (
  searchText,
  searchFilters,
  start,
  size = 10,
) => {
  let textQuery = '';
  if (searchText && searchText.length > 0) {
    textQuery = searchText.toLowerCase();
  }

  let filterQuery = '';
  if (searchFilters && searchFilters.length > 0) {
    const filterCons = searchFilters.map((filter) => {
      let newFilter = '';
      newFilter += `\\'${filter.toLowerCase()}\\'`;
      return newFilter;
    });

    filterQuery = filterCons.join(' ');
  }

  const query = `${textQuery} ${filterQuery}`;

  const esdslQuery = {
    query: {
      bool: {
        must_not: [
          {
            exists: {
              field: 'seagull_obs_dataset_id_i',
            },
          },
        ],
        must: [],
      },
    },
  };

  if (query.trim().length > 0) {
    esdslQuery.query.bool.must = [
      {
        query_string: {
          analyze_wildcard: true,
          query: `${query}`,
        },
      },
    ];
  }

  return getMetaDataFromGeoPortal(`/opensearch?f=json&from=${start}&size=${size}&esdsl=${encodeURIComponent(JSON.stringify(esdslQuery))}`);
};

export const getOrganizationDatasets = (organizationName, from = 1) => {
  if (!organizationName) return null;

  const query = {
    query: {
      bool: {
        must: [
          {
            term: {
              contact_organizations_s: organizationName,
            },
          },
        ],
        must_not: [
          {
            exists: {
              field: 'seagull_obs_dataset_id_i',
            },
          },
        ],
      },
    },
  };

  return axios
    .get(
      `${appConfig.metadataURL}/opensearch?f=json&from=${from}&size=99&esdsl=${encodeURIComponent(
        JSON.stringify(query),
      )}`,
    )
    .then(({ data }) => data)
    .catch((err) => Promise.reject(err));
};

export const getPlatformEvents = (platformId) => cdnApiClient.request(cdnApiPath(`platforms/${platformId}/events`));

export const getQartodConfiguration = () => cdnApiClient.request(cdnApiPath('qartod-configuration'));

export const postObsDatasetMessage = (obsDatasetId, message) => cdnApiClient.request(
  cdnApiPath(`obs-datasets/${obsDatasetId}/messages`),
  { method: 'POST', body: { message } },
);

export const deleteObsDatasetMessage = (obsDatasetId, messageId) => cdnApiClient.request(
  cdnApiPath(`obs-datasets/${obsDatasetId}/messages/${messageId}`),
  { method: 'DELETE' },
);

export const postPlatformEvent = (platformId, payload) => cdnApiClient.request(
  cdnApiPath(`platforms/${platformId}/events`),
  { method: 'POST', body: payload },
);

export const postDeploymentSite = (obsDatasetId, payload) => cdnApiClient.request(
  cdnApiPath(`obs-datasets/${obsDatasetId}/deployment-sites`),
  { method: 'POST', body: payload },
);

export const putObsDataset = (obsDatasetId, payload) => cdnApiClient.request(
  cdnApiPath(`obs-datasets/${obsDatasetId}`),
  { method: 'PUT', body: payload },
);

export const putPlatform = (platformId, payload) => cdnApiClient.request(
  cdnApiPath(`platforms/${platformId}`),
  { method: 'PUT', body: payload },
);

export const postDatasetMetadata = (obsDatasetId, payload) => cdnApiClient.request(
  cdnApiPath(`obs-datasets/${obsDatasetId}/metadata`),
  { method: 'POST', body: payload },
);

export const getParameterMessage = (parameterId) => cdnApiClient.request(cdnApiPath(`parameters/${parameterId}/messages`));

export const postParameterMessage = (parameterId, message) => cdnApiClient.request(
  cdnApiPath(`parameters/${parameterId}/messages`),
  { method: 'POST', body: { message } },
);

export const deleteParameterMessage = (parameterId, parameterMessageId) => cdnApiClient.request(
  cdnApiPath(`parameters/${parameterId}/messages/${parameterMessageId}`),
  { method: 'DELETE' },
);

export const updateParameter = (parameterId, payload) => cdnApiClient.request(
  cdnApiPath(`parameters/${parameterId}`),
  { method: 'PUT', body: payload },
);

export const getObsDatasetViewsAnalytics = (obsDatasetId, startDate, endDate) => {
  const queryString = objectToQuerystring({ startDate, endDate });
  return cdnApiClient.request(cdnApiPath(`obs-datasets/${obsDatasetId}/analytics/views?${queryString}`));
};

export const getObsDatasetDownloadsAnalytics = (obsDatasetId, startDate, endDate) => {
  const queryString = objectToQuerystring({ startDate, endDate });
  return cdnApiClient.request(cdnApiPath(`obs-datasets/${obsDatasetId}/analytics/downloads?${queryString}`));
};

export const getWebcams = () => cdnApiClient.request(cdnApiPath('webcams'));
